<script setup lang="ts">
import { useVModel } from '@vueuse/core'
import type { HTMLAttributes } from 'vue'
import { cn } from '../../../lib/utils'

/**
 * #973 — shadcn-vue `Textarea`, added alongside `input` so multi-line fields (starting with the
 * chat composer) stop hand-rolling a bare `<textarea>` and ad hoc Tailwind classes. It carries the
 * same field recipe `Input` does — one border token, one focus-visible ring, one disabled
 * treatment — so a multi-line field reads as the same control as a single-line one. Only the
 * single-line-specific bits differ: no fixed height and no `file:` rules. The shared recipe is the
 * brand control voice (BG §19.3): mono face, system tracking and an uppercase placeholder. It sets no
 * outline suppression, so the `@layer base` `:focus-visible` ring shows (BG §22.3).
 *
 * Height comes from the caller's `rows` alone. It deliberately leaves out shadcn's
 * `field-sizing-content` (auto-grow as you type) and its `min-h-16` floor: auto-expanding is a new
 * interaction pattern #973's triage ruled out of scope, and the floor would override `rows="2"`.
 */
const props = defineProps<{
    defaultValue?: string
    modelValue?: string
    class?: HTMLAttributes['class']
    disabled?: boolean
}>()

const emits = defineEmits<{
    (e: 'update:modelValue', payload: string): void
}>()

const modelValue = useVModel(props, 'modelValue', emits, {
    passive: true,
    defaultValue: props.defaultValue,
})
</script>

<template>
  <textarea
    v-model="modelValue"
    data-slot="textarea"
    :disabled="disabled"
    :class="cn(
      'dark:bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 disabled:bg-input/50 dark:disabled:bg-input/80 rounded-lg border bg-transparent px-2.5 py-1.5 font-mono tracking-[var(--tracking-system)] text-base transition-colors focus-visible:ring-3 aria-invalid:ring-3 md:text-sm w-full min-w-0 placeholder:text-muted-foreground placeholder:uppercase disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
      props.class,
    )"
  />
</template>
